import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { formatDateInTimeZone, getUserTimezone } from '../../shared/utils/date.util';

/**
 * Displays a compact payment information card showing card brand, masked card number, transaction date/time, transaction ID, billing address, and contact information.
 * @component
 * @selector app-payment-info-card
 * @example
 *   <app-payment-info-card
 *     [brandIcon]="'assets/images/visa.png'"
 *     [cardMask]="'XXX-3324'"
 *     [dateTime]="'2026-03-17T14:30:00Z'"
 *     [transactionId]="'TXN123456'"
 *     [billingAddress]="'123 Main St, Springfield, IL'"
 *     [contact]="'john.doe@example.com'">
 *   </app-payment-info-card>
 * @input {string} brandIcon - URL or path to the payment brand icon image. Default: 'assets/images/master_card.png'.
 * @input {string} brandName - Alt text for the brand icon (e.g. 'Visa'). Default: 'Card'.
 * @input {string} cardMask - Masked card number string displayed on the card (e.g., 'XXX-3324'). Default: 'XXX-3324'.
 * @input {string} dateTime - Transaction date/time as a string. Can be a full date-time or already formatted string containing 'AM'/'PM'. Used by formattedDate to present a user-friendly date when possible.
 * @input {string} transactionId - Identifier for the transaction to display.
 * @input {string} billingAddress - Billing address associated with the payment.
 * @input {string} contact - Contact information related to the payment (email or phone).
 * Key methods:
 *   - get formattedDate(): string - Returns a location-timezone formatted date (e.g., 'Mar 17, 2026') when dateTime is a parsable raw timestamp. Returns an empty string when dateTime is empty, already contains 'AM'/'PM' (parent-formatted), or is invalid — the template prefers formattedDate over raw dateTime.
 * Important notes:
 *   - This is a standalone Angular component that imports CommonModule.
 *   - No output events are emitted by this component; it is purely presentational.
 */
@Component({
  selector: 'app-payment-info-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-info-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./payment-info-card.component.scss'],
})
export class PaymentInfoCardComponent {
  @Input() brandIcon = 'assets/images/master_card.png';
  /** Alt text for the brand icon, e.g. `Visa`. */
  @Input() brandName = 'Card';
  @Input() cardMask = 'XXX-3324';
  @Input() dateTime = '';
  @Input() transactionId = '';
  @Input() billingAddress = '';
  @Input() contact = '';

  get formattedDate(): string {
    if (!this.dateTime) return '';
    // If dateTime already contains formatted date with time, return empty (use dateTime directly)
    if (this.dateTime.includes('AM') || this.dateTime.includes('PM')) {
      return '';
    }
    return formatDateInTimeZone(this.dateTime, getUserTimezone(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
